import React, { useCallback, useMemo } from "react";

import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { endOfToday, startOfToday, subDays } from "date-fns";
import { Controller, useFormContext } from "react-hook-form";

import { CodeInput } from "src/base-components/CodeInput/CodeInput";
import { EditorBoundsProvider } from "src/base-components/CodeInput/useEditorBounds";
import { DateRangePicker } from "src/base-components/DateRangePicker";
import { Icon } from "src/base-components/Icon";
import { OperatorDropdown } from "src/base-components/OperatorDropdown";
import { Select } from "src/base-components/Select";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import { Operator } from "src/constants/operators";
import { EventFieldsDropdown } from "src/eventsCatalogue/filters/EventFieldsDropdown";
import { getAvailableOperatorsOptions } from "src/eventsCatalogue/filters/operators";
import {
  EventFilterFormData,
  Junction,
} from "src/eventsCatalogue/filters/types";
import {
  getEventProperties,
  parseJsonArray,
} from "src/eventsCatalogue/filters/utils";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { findEventSchema } from "src/eventsCatalogue/utils";
import { useTimeWindow } from "src/utils/timeWindow";

type PropertyName = ReturnType<typeof getEventProperties>[number]["name"];

type EventFilterConditionProps = {
  eventType: string;
  groupIndex: number;
  conditionIndex: number;
  mode: "where" | "junction";
  onRemove: () => void;
  onJunctionChange?: (junction: Junction) => void;
  canRemove?: boolean;
};

// Reusable component for OperatorDropdown wrapped in CodeInput
type OperatorDropdownInputProps = {
  groupIndex: number;
  conditionIndex: number;
  operatorOptions: Array<{
    key: string;
    value: typeof import("src/constants/operators").OPERATORS[keyof typeof import("src/constants/operators").OPERATORS];
  }>;
  codeInputValue?: string;
  codeInputReadOnly?: boolean;
  onValueChange: (value: string) => void;
  variant?: "cell";
  placeholder?: string;
  error?: string | boolean;
};

const OperatorDropdownInput: React.FC<OperatorDropdownInputProps> = ({
  groupIndex,
  conditionIndex,
  operatorOptions,
  codeInputValue = "",
  codeInputReadOnly = false,
  onValueChange,
  variant,
  placeholder,
  error,
}) => {
  const operatorPath =
    `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.operator` as const;
  const dataLoc = `group-${groupIndex}-condition-${conditionIndex}-operator`;

  const codeInput = (
    <CodeInput
      dataLoc={`group-${groupIndex}-condition-${conditionIndex}-value`}
      error={error}
      placeholder={placeholder}
      readOnly={codeInputReadOnly}
      value={codeInputValue}
      variant={variant}
      onChange={onValueChange}
    >
      <Controller
        name={operatorPath}
        render={({ field: operatorField }) => (
          <OperatorDropdown
            dataLoc={dataLoc}
            options={operatorOptions}
            value={operatorField.value as Operator}
            onChange={operatorField.onChange}
          />
        )}
      />
    </CodeInput>
  );

  return <EditorBoundsProvider maxX={100}>{codeInput}</EditorBoundsProvider>;
};

export const EventFilterCondition: React.FC<EventFilterConditionProps> = ({
  eventType,
  groupIndex,
  conditionIndex,
  mode,
  onRemove,
  onJunctionChange,
  canRemove = true,
}) => {
  const form = useFormContext<EventFilterFormData>();
  const { data: eventSchemas } = useEventSchemas();
  const currentEvent = findEventSchema(eventType, eventSchemas);

  const { onDateRangePickerChange, timeWindow } = useTimeWindow(
    {
      from: subDays(startOfToday(), 7),
      to: endOfToday(),
    },
    `filter-condition-time-window-${eventType}`,
  );

  const eventProperties = useMemo(
    () => getEventProperties(currentEvent),
    [currentEvent],
  );

  const selectedField = form.watch(
    `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.field`,
  );

  const selectedFieldType = useMemo(() => {
    if (!selectedField) {
      return undefined;
    }
    const property = eventProperties.find((p) => p.name === selectedField);
    return property?.datatype;
  }, [selectedField, eventProperties]);

  const operatorOptions = useMemo(
    () => getAvailableOperatorsOptions(selectedFieldType),
    [selectedFieldType],
  );

  const isDateOrDatetimeField =
    selectedFieldType === PropertyDefinitionOutputTypeEnum.DATE ||
    selectedFieldType === PropertyDefinitionOutputTypeEnum.DATETIME;

  const isTagsField =
    selectedFieldType === PropertyDefinitionOutputTypeEnum.TAGS;

  const isEnumField =
    selectedFieldType === PropertyDefinitionOutputTypeEnum.ENUM;

  const isBooleanField =
    selectedFieldType === PropertyDefinitionOutputTypeEnum.BOOLEAN;

  const isRelationField =
    selectedFieldType === PropertyDefinitionOutputTypeEnum.RELATION;

  const getProperty = useCallback(
    (field: string) => currentEvent?.properties?.[field],
    [currentEvent?.properties],
  );

  // Get tag options from enum_schema (only computed for tags fields)
  const tagOptions = useMemo(() => {
    if (!isTagsField || !selectedField || !getProperty(selectedField)) {
      return [];
    }
    const property = getProperty(selectedField);
    return (
      property?.enum_schema?.values.map((enumValue) => ({
        key: enumValue.value,
        value: enumValue.value,
      })) ?? []
    );
  }, [isTagsField, selectedField, getProperty]);

  // Get enum options from enum_schema (only computed for enum fields)
  const enumOptions = useMemo(() => {
    if (!isEnumField || !selectedField || !getProperty(selectedField)) {
      return [];
    }
    const property = getProperty(selectedField);
    return (
      property?.enum_schema?.values.map((enumValue) => ({
        key: enumValue.value,
        value: enumValue.value,
      })) ?? []
    );
  }, [isEnumField, selectedField, getProperty]);

  // Helper to reset operator and value when field changes
  const handleFieldChange = (
    newField: PropertyName,
    currentField: PropertyName,
  ) => {
    if (currentField !== newField && newField !== "") {
      const selectedProperty = eventProperties.find((p) => p.name === newField);
      const selectedFieldType = selectedProperty?.datatype;
      const fieldOperatorOptions =
        getAvailableOperatorsOptions(selectedFieldType);
      const firstOperator = fieldOperatorOptions[0]?.key ?? "eq";
      form.setValue(
        `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.operator`,
        firstOperator,
      );

      // Set default value based on field type
      if (
        selectedFieldType === PropertyDefinitionOutputTypeEnum.DATE ||
        selectedFieldType === PropertyDefinitionOutputTypeEnum.DATETIME
      ) {
        // Default to last 7 days for date/datetime fields
        const defaultDateRange = {
          from: timeWindow?.from?.toISOString(),
          to: timeWindow?.to?.toISOString(),
        };
        form.setValue(
          `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.value`,
          JSON.stringify(defaultDateRange),
        );
      } else {
        form.setValue(
          `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.value`,
          "",
        );
      }
    }
  };

  return (
    <div className="flex items-center gap-x-2">
      <div className="basis-[72px]">
        {mode === "where" ? (
          <p className="font-inter-medium-12px text-gray-700">Where</p>
        ) : (
          <Controller
            name={`filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.junction`}
            render={({ field }) => (
              <EventFieldsDropdown
                dataLoc={`group-${groupIndex}-condition-${conditionIndex}-junction`}
                disabled={conditionIndex > 1}
                eventType={eventType}
                selectedKey={field.value}
                type="junction"
                onSelect={(key) => {
                  field.onChange(key);
                  onJunctionChange?.(key as Junction);
                }}
              />
            )}
          />
        )}
      </div>
      <div className="w-40">
        <Controller
          name={`filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.field`}
          render={({ field }) => (
            <EventFieldsDropdown
              dataLoc={`group-${groupIndex}-condition-${conditionIndex}-field`}
              eventType={eventType}
              selectedKey={field.value}
              type="field"
              onSelect={(newField) => {
                handleFieldChange(newField, field.value);
                field.onChange(newField);
              }}
            />
          )}
        />
      </div>
      <div className="flex-1">
        <Controller
          name={`filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.value`}
          render={({ field, fieldState }) => {
            const currentOperator = form.watch(
              `filter_condition.conditions.${groupIndex}.conditions.${conditionIndex}.operator`,
            );
            const isNoneOperator = currentOperator === "is_none";

            if (isTagsField) {
              const selectedTagValues = parseJsonArray(field.value);
              return (
                <div className="flex items-center gap-x-2">
                  <div className="w-24">
                    <OperatorDropdownInput
                      codeInputReadOnly={true}
                      conditionIndex={conditionIndex}
                      groupIndex={groupIndex}
                      operatorOptions={operatorOptions}
                      onValueChange={field.onChange}
                    />
                  </div>
                  {!isNoneOperator && (
                    <div className="flex-1">
                      <Select
                        dataLoc={`group-${groupIndex}-condition-${conditionIndex}-tags`}
                        options={tagOptions}
                        value={selectedTagValues}
                        multiple
                        onChange={(values) => {
                          const jsonValue =
                            values.length > 0 ? JSON.stringify(values) : "";
                          field.onChange(jsonValue);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            }

            if (isBooleanField) {
              return (
                <div className="flex items-center gap-x-2">
                  <div className="w-24">
                    <OperatorDropdownInput
                      codeInputReadOnly={true}
                      conditionIndex={conditionIndex}
                      groupIndex={groupIndex}
                      operatorOptions={operatorOptions}
                      onValueChange={field.onChange}
                    />
                  </div>
                  {!isNoneOperator && (
                    <div className="flex-1">
                      <Select
                        dataLoc={`group-${groupIndex}-condition-${conditionIndex}-enum`}
                        options={[
                          { key: "True", value: "True" },
                          { key: "False", value: "False" },
                        ]}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            }

            if (isEnumField) {
              const selectedEnumValues = parseJsonArray(field.value);

              return (
                <div className="flex items-center gap-x-2">
                  <div className="w-24">
                    <OperatorDropdownInput
                      codeInputReadOnly={true}
                      conditionIndex={conditionIndex}
                      groupIndex={groupIndex}
                      operatorOptions={operatorOptions}
                      onValueChange={field.onChange}
                    />
                  </div>
                  {!isNoneOperator && (
                    <div className="flex-1">
                      <Select
                        dataLoc={`group-${groupIndex}-condition-${conditionIndex}-enum`}
                        options={enumOptions}
                        value={selectedEnumValues}
                        multiple
                        onChange={(values) => {
                          const jsonValue =
                            values.length > 0 ? JSON.stringify(values) : "";
                          field.onChange(jsonValue);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            }

            if (isDateOrDatetimeField) {
              return (
                <DateRangePicker
                  value={timeWindow}
                  usePredefinedDates
                  onChange={onDateRangePickerChange}
                />
              );
            }

            return (
              <OperatorDropdownInput
                codeInputReadOnly={isNoneOperator}
                codeInputValue={field.value}
                conditionIndex={conditionIndex}
                error={fieldState.error?.message}
                groupIndex={groupIndex}
                operatorOptions={operatorOptions}
                placeholder={
                  isRelationField
                    ? `Enter ${getProperty(selectedField)?.display_name} ID`
                    : "Enter value"
                }
                onValueChange={field.onChange}
              />
            );
          }}
        />
      </div>
      <div className="basis-[18px]">
        {canRemove && (
          <Icon
            color="text-gray-500 hover:text-gray-700 cursor-pointer"
            dataLoc={`group-${groupIndex}-condition-${conditionIndex}-delete`}
            icon={faTrashCan}
            size="2xs"
            onClick={onRemove}
          />
        )}
      </div>
    </div>
  );
};
